import React, { useState } from 'react';
import { Popup, Toast } from 'antd-mobile';
import styled, { keyframes, css, ThemeProvider } from 'styled-components';
import { useAppSelector, useAppDispatch } from '../../hooks/reduxHooks';
import {
  selectShowTopupModal,
  selectSelectedTopupAmount,
  selectCustomTopupAmount,
  selectWalletLoading,
  selectCurrentPayment,
  hideTopupModal,
  setSelectedTopupAmount,
  setCustomTopupAmount,
  initiateKhaltiTopup,
  resetPaymentState,
} from '../../store/slices/walletSlice';
import { walletService } from '../../services/api';

// ─── Theme tokens ───────────────────────────────────────────────────────────

type Scheme = 'dark' | 'light';

type Tokens = {
  scheme: Scheme;
  ink: {
    base: string;
    raised: string;
    edge: string;
    edgeStrong: string;
  };
  text: {
    primary: string;
    muted: string;
    faint: string;
    onAccent: string;
  };
  accent: {
    khalti: string;
    khaltiDeep: string;
    brass: string;
    brassDeep: string;
    alert: string;
    ok: string;
  };
  surface: {
    cardIdle: string;
    cardSelected: string;
    inputIdle: string;
    inputFocused: string;
  };
  grain: {
    blend: 'overlay' | 'multiply';
    opacity: number;
  };
  glow: {
    selectionRing: string;
    inputRing: string;
  };
};

const darkTokens: Tokens = {
  scheme: 'dark',
  ink: {
    base: '#0F0A1A',
    raised: '#1A1428',
    edge: 'rgba(245, 241, 232, 0.08)',
    edgeStrong: 'rgba(245, 241, 232, 0.16)',
  },
  text: {
    primary: '#F5F1E8',
    muted: 'rgba(245, 241, 232, 0.55)',
    faint: 'rgba(245, 241, 232, 0.35)',
    onAccent: '#F5F1E8',
  },
  accent: {
    khalti: '#7B3FE4',
    khaltiDeep: '#5A2D82',
    brass: '#D4A75C',
    brassDeep: '#A88340',
    alert: '#FF6B6B',
    ok: '#7DD181',
  },
  surface: {
    cardIdle: 'rgba(245, 241, 232, 0.025)',
    cardSelected:
      'linear-gradient(135deg, rgba(123, 63, 228, 0.22) 0%, rgba(90, 45, 130, 0.12) 100%)',
    inputIdle: 'rgba(245, 241, 232, 0.03)',
    inputFocused: 'rgba(123, 63, 228, 0.06)',
  },
  grain: {
    blend: 'overlay',
    opacity: 0.05,
  },
  glow: {
    selectionRing: 'rgba(123, 63, 228, 0.18)',
    inputRing: 'rgba(123, 63, 228, 0.15)',
  },
};

const lightTokens: Tokens = {
  scheme: 'light',
  ink: {
    base: '#F8F2E2',
    raised: '#FFFCF5',
    edge: 'rgba(26, 20, 40, 0.10)',
    edgeStrong: 'rgba(26, 20, 40, 0.20)',
  },
  text: {
    primary: '#1A1428',
    muted: 'rgba(26, 20, 40, 0.62)',
    faint: 'rgba(26, 20, 40, 0.42)',
    onAccent: '#FFFCF5',
  },
  accent: {
    // Khalti brand purple holds at full saturation on cream — feels on-brand
    // without the dark theme's softening for contrast.
    khalti: '#5A2D82',
    khaltiDeep: '#3E1F5C',
    // Deepened brass so it reads against cream instead of vanishing.
    brass: '#8B6F35',
    brassDeep: '#5C4922',
    alert: '#B43A37',
    ok: '#3F8E45',
  },
  surface: {
    cardIdle: 'rgba(26, 20, 40, 0.025)',
    cardSelected:
      'linear-gradient(135deg, rgba(90, 45, 130, 0.10) 0%, rgba(139, 111, 53, 0.06) 100%)',
    inputIdle: 'rgba(26, 20, 40, 0.025)',
    inputFocused: 'rgba(90, 45, 130, 0.06)',
  },
  grain: {
    // multiply darkens the cream, matching the paper texture intent
    blend: 'multiply',
    opacity: 0.06,
  },
  glow: {
    selectionRing: 'rgba(90, 45, 130, 0.16)',
    inputRing: 'rgba(90, 45, 130, 0.16)',
  },
};

// styled-components theme augmentation so theme.x is typed
declare module 'styled-components' {
  export interface DefaultTheme extends Tokens {}
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const showToast = (message: string) => {
  try {
    Toast.show(message);
  } catch {
    console.log(message);
  }
};

// ─── Animations ─────────────────────────────────────────────────────────────

const sheetEnter = keyframes`
  from { transform: translateY(8px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const stagger = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const gloss = keyframes`
  0%   { transform: translateX(-110%) skewX(-18deg); }
  60%  { transform: translateX(110%)  skewX(-18deg); }
  100% { transform: translateX(110%)  skewX(-18deg); }
`;

const ringSpin = keyframes`
  to { transform: rotate(360deg); }
`;

// ─── Surface ────────────────────────────────────────────────────────────────

const Sheet = styled.div`
  position: relative;
  background: ${({ theme }) =>
    theme.scheme === 'dark'
      ? `radial-gradient(120% 80% at 0% 0%, rgba(123, 63, 228, 0.18), transparent 55%),
         radial-gradient(120% 80% at 100% 0%, rgba(212, 167, 92, 0.10), transparent 60%),
         linear-gradient(180deg, ${theme.ink.raised} 0%, ${theme.ink.base} 60%)`
      : `radial-gradient(140% 90% at 0% 0%, rgba(90, 45, 130, 0.07), transparent 55%),
         radial-gradient(140% 90% at 100% 0%, rgba(139, 111, 53, 0.06), transparent 60%),
         linear-gradient(180deg, ${theme.ink.raised} 0%, ${theme.ink.base} 100%)`};
  color: ${({ theme }) => theme.text.primary};
  padding: 10px 24px max(28px, env(safe-area-inset-bottom)) 24px;
  font-family: 'Geist', system-ui, sans-serif;
  font-feature-settings: 'ss01', 'cv11';
  border-top-left-radius: 28px;
  border-top-right-radius: 28px;
  overflow: hidden;
  animation: ${sheetEnter} 320ms cubic-bezier(0.2, 0.8, 0.2, 1);
  box-shadow: ${({ theme }) =>
    theme.scheme === 'light'
      ? '0 -1px 0 rgba(26, 20, 40, 0.08), 0 -20px 60px -20px rgba(26, 20, 40, 0.18)'
      : 'none'};
`;

const Grain = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: ${({ theme }) => theme.grain.opacity};
  mix-blend-mode: ${({ theme }) => theme.grain.blend};
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' seed='5'/><feColorMatrix values='0 0 0 0 1, 0 0 0 0 1, 0 0 0 0 1, 0 0 0 0.8 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>");
`;

const DragHandle = styled.div`
  width: 44px;
  height: 4px;
  background: ${({ theme }) => theme.ink.edgeStrong};
  border-radius: 2px;
  margin: 4px auto 22px;
`;

// Visible close affordance for keyboard / screen-reader users — mask-tap
// alone is a discoverability problem on mobile. Mirrors the rider sheet
// CloseButton (from sheetPrimitives.tsx) but stays in Khalti's cream /
// brass palette so it doesn't break the modal's visual identity.
const CloseButton = styled.button.attrs({
  type: 'button' as const,
  'aria-label': 'Close',
})`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 36px;
  height: 36px;
  border: 1px solid ${({ theme }) => theme.ink.edge};
  border-radius: 50%;
  background: ${({ theme }) => theme.surface.inputIdle};
  color: ${({ theme }) => theme.accent.brass};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-family: 'Fraunces', serif;
  font-size: 20px;
  line-height: 1;
  padding: 0;
  z-index: 2;
  transition: background 160ms ease, color 160ms ease, border-color 160ms ease;

  &:hover {
    background: ${({ theme }) => theme.surface.inputFocused};
    color: ${({ theme }) => theme.accent.brassDeep};
    border-color: ${({ theme }) => theme.ink.edgeStrong};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => theme.accent.brass}55;
    color: ${({ theme }) => theme.accent.brassDeep};
  }
`;

const BrassThread = styled.div`
  position: absolute;
  top: 22px;
  bottom: 22px;
  left: 0;
  width: 2px;
  background: ${({ theme }) => `linear-gradient(
    180deg,
    transparent 0%,
    ${theme.accent.brassDeep} 30%,
    ${theme.accent.brass} 50%,
    ${theme.accent.brassDeep} 70%,
    transparent 100%
  )`};
  opacity: ${({ theme }) => (theme.scheme === 'light' ? 0.65 : 0.5)};
`;

// ─── Header ─────────────────────────────────────────────────────────────────

const Header = styled.header`
  position: relative;
  margin-bottom: 26px;
  animation: ${stagger} 380ms cubic-bezier(0.2, 0.8, 0.2, 1) 60ms backwards;
`;

const Eyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.text.faint};
  margin-bottom: 10px;
`;

const RupeeMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: 1px solid ${({ theme }) => theme.accent.brassDeep};
  color: ${({ theme }) => theme.accent.brass};
  border-radius: 50%;
  font-family: 'Fraunces', serif;
  font-weight: 500;
  font-size: 11px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
`;

const Title = styled.h2`
  font-family: 'Fraunces', 'Times New Roman', serif;
  font-weight: 400;
  font-size: 34px;
  font-variation-settings: 'opsz' 144, 'SOFT' 100;
  letter-spacing: -0.025em;
  line-height: 1;
  margin: 0;
  color: ${({ theme }) => theme.text.primary};
`;

const TitleAccent = styled.em`
  font-style: italic;
  font-variation-settings: 'opsz' 144, 'SOFT' 60, 'WONK' 1;
  color: ${({ theme }) => theme.accent.brass};
`;

const KhaltiLockup = styled.div`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.text.muted};
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ theme }) => theme.accent.khalti};
    box-shadow: ${({ theme }) =>
      theme.scheme === 'light'
        ? '0 0 6px rgba(90, 45, 130, 0.4)'
        : '0 0 8px rgba(123, 63, 228, 0.6)'};
  }
`;

// ─── Sections ───────────────────────────────────────────────────────────────

const Section = styled.section<{ $delay?: number }>`
  margin-bottom: 22px;
  animation: ${stagger} 380ms cubic-bezier(0.2, 0.8, 0.2, 1)
    ${p => 120 + (p.$delay ?? 0)}ms backwards;
`;

const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.text.faint};
  margin-bottom: 14px;

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${({ theme }) =>
      `linear-gradient(90deg, ${theme.ink.edge}, transparent)`};
  }
`;

// ─── Amount cards ───────────────────────────────────────────────────────────

const AmountGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

const AmountCard = styled.button<{ $selected: boolean }>`
  position: relative;
  padding: 16px 16px 14px;
  background: ${({ theme, $selected }) =>
    $selected ? theme.surface.cardSelected : theme.surface.cardIdle};
  border: 1px solid
    ${({ theme, $selected }) =>
      $selected ? theme.accent.khalti : theme.ink.edge};
  border-radius: 14px;
  color: ${({ theme }) => theme.text.primary};
  text-align: left;
  cursor: pointer;
  transition:
    transform 160ms cubic-bezier(0.2, 0, 0, 1),
    background 200ms ease,
    border-color 200ms ease;
  min-height: 60px;
  font-family: inherit;
  overflow: hidden;

  &:active {
    transform: scale(0.985);
  }

  ${({ $selected, theme }) =>
    $selected &&
    css`
      box-shadow:
        0 0 0 3px ${theme.glow.selectionRing},
        inset 0 1px 0
          ${theme.scheme === 'light'
            ? 'rgba(255, 255, 255, 0.6)'
            : 'rgba(255, 255, 255, 0.06)'};

      &::after {
        content: '';
        position: absolute;
        top: 10px;
        right: 10px;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: ${theme.accent.brass};
        box-shadow: 0 0 8px ${theme.accent.brass};
      }
    `}
`;

const Currency = styled.span`
  display: block;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.2em;
  color: ${({ theme }) => theme.text.faint};
  margin-bottom: 4px;
`;

const Value = styled.span`
  display: block;
  font-family: 'Fraunces', serif;
  font-weight: 500;
  font-size: 22px;
  font-variation-settings: 'opsz' 144;
  letter-spacing: -0.02em;
  line-height: 1.05;
`;

// ─── Custom amount ──────────────────────────────────────────────────────────

const CustomInputWrap = styled.div<{ $focus: boolean; $error: boolean }>`
  display: flex;
  align-items: center;
  background: ${({ theme, $focus, $error }) =>
    $focus && !$error ? theme.surface.inputFocused : theme.surface.inputIdle};
  border: 1px solid
    ${({ theme, $focus, $error }) =>
      $error ? theme.accent.alert : $focus ? theme.accent.khalti : theme.ink.edge};
  border-radius: 14px;
  padding: 0 18px;
  height: 60px;
  transition: border-color 200ms ease, background 200ms ease;

  ${({ $focus, $error, theme }) =>
    $focus &&
    !$error &&
    css`
      box-shadow: 0 0 0 3px ${theme.glow.inputRing};
    `}
`;

const CurrencyPrefix = styled.span`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.2em;
  color: ${({ theme }) => theme.text.muted};
  border-right: 1px solid ${({ theme }) => theme.ink.edge};
  padding-right: 14px;
  margin-right: 14px;
`;

const CustomInput = styled.input`
  flex: 1;
  min-width: 0;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.text.primary};
  font-family: 'Fraunces', serif;
  font-size: 22px;
  font-weight: 500;
  font-variation-settings: 'opsz' 144;
  letter-spacing: -0.015em;
  outline: none;
  caret-color: ${({ theme }) => theme.accent.khalti};

  &::placeholder {
    color: ${({ theme }) => theme.text.faint};
    font-family: 'Fraunces', serif;
    font-style: italic;
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

const ErrorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 12px 4px 0;
  font-family: 'Geist', sans-serif;
  font-size: 12px;
  color: ${({ theme }) => theme.accent.alert};

  &::before {
    content: '';
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: ${({ theme }) => theme.accent.alert};
  }
`;

// ─── Pay button ─────────────────────────────────────────────────────────────

const PayButton = styled.button<{ $disabled: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 60px;
  padding: 0 22px;
  margin-top: 26px;
  background: ${({ theme }) =>
    `linear-gradient(135deg, ${theme.accent.khalti} 0%, ${theme.accent.khaltiDeep} 100%)`};
  border: 1px solid
    ${({ theme }) =>
      theme.scheme === 'light'
        ? 'rgba(255, 255, 255, 0.18)'
        : 'rgba(255, 255, 255, 0.08)'};
  border-radius: 16px;
  color: ${({ theme }) => theme.text.onAccent};
  font-family: 'Geist', sans-serif;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.01em;
  cursor: ${p => (p.$disabled ? 'not-allowed' : 'pointer')};
  overflow: hidden;
  transition: transform 180ms cubic-bezier(0.2, 0, 0, 1), opacity 200ms ease;
  animation: ${stagger} 380ms cubic-bezier(0.2, 0.8, 0.2, 1) 220ms backwards;
  box-shadow: ${({ theme }) =>
    theme.scheme === 'light'
      ? '0 8px 24px -8px rgba(90, 45, 130, 0.45)'
      : 'none'};

  ${p =>
    p.$disabled &&
    css`
      opacity: 0.45;
      filter: saturate(0.6);
    `}

  &:not(:disabled):hover {
    transform: translateY(-1px);
  }
  &:not(:disabled):active {
    transform: translateY(0) scale(0.99);
  }

  &::before {
    content: '';
    position: absolute;
    inset: -2px -40%;
    background: linear-gradient(
      90deg,
      transparent 30%,
      rgba(255, 255, 255, 0.18) 50%,
      transparent 70%
    );
    transform: translateX(-110%) skewX(-18deg);
    animation: ${gloss} 2.6s cubic-bezier(0.4, 0, 0.6, 1) 600ms 1 forwards;
    pointer-events: none;
  }
`;

const PayLabel = styled.span`
  display: flex;
  align-items: baseline;
  gap: 8px;
  z-index: 1;
`;

const PayLabelText = styled.span`
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-size: 11px;
  font-family: 'Geist Mono', ui-monospace, monospace;
  color: rgba(255, 255, 255, 0.7);
`;

const PayLabelAmount = styled.span`
  font-family: 'Fraunces', serif;
  font-size: 22px;
  font-weight: 500;
  font-variation-settings: 'opsz' 144;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.text.onAccent};
`;

const PayChevron = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: ${({ theme }) => theme.text.onAccent};
  font-family: 'Geist', sans-serif;
  font-size: 16px;
  z-index: 1;
  transition: transform 180ms cubic-bezier(0.2, 0, 0, 1);

  ${PayButton}:not(:disabled):hover & {
    transform: translateX(2px);
  }
`;

// ─── Footer ─────────────────────────────────────────────────────────────────

const Footer = styled.footer`
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid ${({ theme }) => theme.ink.edge};
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.text.faint};
  animation: ${stagger} 380ms cubic-bezier(0.2, 0.8, 0.2, 1) 280ms backwards;
`;

const FooterDot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  margin-right: 8px;
  background: ${p => p.$color};
`;

// ─── Status views ───────────────────────────────────────────────────────────

const StatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 12px 0 8px;
  animation: ${stagger} 380ms cubic-bezier(0.2, 0.8, 0.2, 1) backwards;
`;

const StatusRing = styled.div<{ $variant: 'processing' | 'success' | 'failed' }>`
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  margin-bottom: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme, $variant }) =>
    $variant === 'success'
      ? theme.scheme === 'light'
        ? 'rgba(63, 142, 69, 0.10)'
        : 'rgba(125, 209, 129, 0.12)'
      : $variant === 'failed'
      ? theme.scheme === 'light'
        ? 'rgba(180, 58, 55, 0.10)'
        : 'rgba(255, 107, 107, 0.12)'
      : theme.scheme === 'light'
      ? 'rgba(90, 45, 130, 0.08)'
      : 'rgba(123, 63, 228, 0.12)'};
  border: 1px solid
    ${({ theme, $variant }) =>
      $variant === 'success'
        ? theme.accent.ok
        : $variant === 'failed'
        ? theme.accent.alert
        : theme.accent.khalti};

  ${({ $variant, theme }) =>
    $variant === 'processing' &&
    css`
      &::before {
        content: '';
        position: absolute;
        inset: -1px;
        border-radius: 50%;
        border: 1px solid transparent;
        border-top-color: ${theme.accent.khalti};
        border-right-color: ${theme.accent.khalti};
        animation: ${ringSpin} 1.2s linear infinite;
      }
    `}
`;

const StatusGlyph = styled.span<{ $variant: 'processing' | 'success' | 'failed' }>`
  font-family: 'Fraunces', serif;
  font-size: 28px;
  line-height: 1;
  color: ${({ theme, $variant }) =>
    $variant === 'success'
      ? theme.accent.ok
      : $variant === 'failed'
      ? theme.accent.alert
      : theme.accent.brass};
`;

const StatusTitle = styled.h3`
  font-family: 'Fraunces', serif;
  font-weight: 400;
  font-size: 24px;
  font-variation-settings: 'opsz' 144;
  letter-spacing: -0.02em;
  margin: 0 0 8px;
  color: ${({ theme }) => theme.text.primary};
`;

const StatusBody = styled.p`
  font-family: 'Geist', sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.text.muted};
  margin: 0 0 24px;
  max-width: 280px;
`;

const StatusButton = styled.button`
  height: 48px;
  padding: 0 28px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.ink.edgeStrong};
  border-radius: 12px;
  color: ${({ theme }) => theme.text.primary};
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 200ms ease, border-color 200ms ease;

  &:hover {
    background: ${({ theme }) =>
      theme.scheme === 'light'
        ? 'rgba(26, 20, 40, 0.04)'
        : 'rgba(245, 241, 232, 0.04)'};
  }
`;

// ─── Component ──────────────────────────────────────────────────────────────

interface KhaltiTopupModalProps {
  theme?: Scheme;
}

const KhaltiTopupModal: React.FC<KhaltiTopupModalProps> = ({ theme = 'light' }) => {
  const dispatch = useAppDispatch();

  const showModal = useAppSelector(selectShowTopupModal);
  const selectedAmount = useAppSelector(selectSelectedTopupAmount);
  const customAmount = useAppSelector(selectCustomTopupAmount);
  const loading = useAppSelector(selectWalletLoading);
  const currentPayment = useAppSelector(selectCurrentPayment);

  const [validationError, setValidationError] = useState<string>('');
  const [customFocus, setCustomFocus] = useState(false);
  const suggestedAmounts = walletService.getSuggestedTopupAmounts();
  const topupLimits = walletService.getTopupLimits();

  const tokens = theme === 'light' ? lightTokens : darkTokens;

  const handleClose = () => {
    dispatch(hideTopupModal());
    dispatch(resetPaymentState());
    setValidationError('');
  };

  const handleAmountSelect = (amount: number) => {
    dispatch(setSelectedTopupAmount(amount));
    setValidationError('');
  };

  const handleCustomAmountChange = (value: string) => {
    const hasNonDigits = /\D/.test(value);
    const numericValue = value.replace(/[^0-9]/g, '');
    dispatch(setCustomTopupAmount(numericValue));

    if (!numericValue) {
      setValidationError(value ? 'Whole NPR amounts only' : '');
      return;
    }
    if (hasNonDigits) {
      setValidationError('Whole NPR amounts only');
      return;
    }
    const validation = walletService.validateTopupAmount(parseInt(numericValue, 10));
    setValidationError(validation.valid ? '' : validation.error || 'Invalid amount');
  };

  const getSelectedAmount = (): number => {
    if (selectedAmount) return selectedAmount;
    if (customAmount) return parseInt(customAmount, 10);
    return 0;
  };

  const validateAmount = (): boolean => {
    const amount = getSelectedAmount();
    if (amount <= 0) {
      setValidationError('Choose or enter an amount');
      return false;
    }
    const validation = walletService.validateTopupAmount(amount);
    if (!validation.valid) {
      setValidationError(validation.error || 'Invalid amount');
      return false;
    }
    return true;
  };

  const handlePayWithKhalti = async () => {
    if (!validateAmount()) return;
    const amount = getSelectedAmount();
    try {
      const payment = await dispatch(initiateKhaltiTopup(amount)).unwrap();
      showToast('Redirecting to Khalti…');
      window.location.assign(payment.payment_url);
    } catch (error: any) {
      console.error('Payment initiation failed:', error);
      showToast(error.message || 'Failed to initiate payment');
    }
  };

  const formatCurrency = (amount: number) => walletService.formatCurrency(amount);

  const amount = getSelectedAmount();
  const canPay = amount > 0 && !validationError && !loading.topup;

  const renderProcessing = () => (
    <StatusContainer>
      <StatusRing $variant="processing" />
      <StatusTitle>Routing to Khalti</StatusTitle>
      <StatusBody>
        We're handing you off to Khalti to complete the payment. Return here
        once you're done.
      </StatusBody>
    </StatusContainer>
  );

  const renderSuccess = () => (
    <StatusContainer>
      <StatusRing $variant="success">
        <StatusGlyph $variant="success">✓</StatusGlyph>
      </StatusRing>
      <StatusTitle>Wallet topped up</StatusTitle>
      <StatusBody>
        {formatCurrency(currentPayment.amount || 0)} added to your wallet.
      </StatusBody>
      <StatusButton onClick={handleClose}>Close</StatusButton>
    </StatusContainer>
  );

  const renderFailed = () => (
    <StatusContainer>
      <StatusRing $variant="failed">
        <StatusGlyph $variant="failed">!</StatusGlyph>
      </StatusRing>
      <StatusTitle>Payment didn't go through</StatusTitle>
      <StatusBody>
        Khalti couldn't complete the transaction. No funds have been moved.
      </StatusBody>
      <StatusButton onClick={() => dispatch(resetPaymentState())}>
        Try again
      </StatusButton>
    </StatusContainer>
  );

  const renderForm = () => (
    <>
      <Header>
        <Eyebrow>
          <RupeeMark>रु</RupeeMark>
          Wallet · Top up
        </Eyebrow>
        <TitleRow>
          <Title>
            Add <TitleAccent>funds</TitleAccent>
          </Title>
          <KhaltiLockup>via Khalti</KhaltiLockup>
        </TitleRow>
      </Header>

      <Section>
        <SectionLabel>Quick amounts</SectionLabel>
        <AmountGrid>
          {suggestedAmounts.map(value => (
            <AmountCard
              key={value}
              type="button"
              $selected={selectedAmount === value}
              onClick={() => handleAmountSelect(value)}
            >
              <Currency>NPR</Currency>
              <Value>{value.toLocaleString()}</Value>
            </AmountCard>
          ))}
        </AmountGrid>
      </Section>

      <Section $delay={60}>
        <SectionLabel>Custom amount</SectionLabel>
        <CustomInputWrap
          $focus={customFocus}
          $error={Boolean(validationError && customAmount)}
        >
          <CurrencyPrefix>NPR</CurrencyPrefix>
          <CustomInput
            placeholder="0"
            value={customAmount}
            onChange={e => handleCustomAmountChange(e.target.value)}
            onFocus={() => setCustomFocus(true)}
            onBlur={() => setCustomFocus(false)}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            min={topupLimits.minAmount}
            max={topupLimits.maxAmount}
          />
        </CustomInputWrap>
        {validationError && <ErrorRow>{validationError}</ErrorRow>}
      </Section>

      <PayButton
        type="button"
        onClick={handlePayWithKhalti}
        disabled={!canPay}
        $disabled={!canPay}
      >
        <PayLabel>
          <PayLabelText>Pay</PayLabelText>
          <PayLabelAmount>
            {amount > 0 ? formatCurrency(amount) : 'NPR —'}
          </PayLabelAmount>
        </PayLabel>
        <PayChevron>→</PayChevron>
      </PayButton>

      <Footer>
        <span>
          <FooterDot $color={tokens.accent.ok} />
          Secured payment
        </span>
        <span>Khalti · KH</span>
      </Footer>
    </>
  );

  return (
    <Popup
      visible={showModal}
      onMaskClick={handleClose}
      position="bottom"
      bodyStyle={{
        background: 'transparent',
        padding: 0,
        boxShadow: 'none',
      }}
      destroyOnClose
    >
      <ThemeProvider theme={tokens}>
        <Sheet>
          <Grain />
          <BrassThread />
          <DragHandle />
          <CloseButton onClick={handleClose}>×</CloseButton>
          {currentPayment.status === 'initiated' || currentPayment.status === 'processing'
            ? renderProcessing()
            : currentPayment.status === 'completed'
            ? renderSuccess()
            : currentPayment.status === 'failed'
            ? renderFailed()
            : renderForm()}
        </Sheet>
      </ThemeProvider>
    </Popup>
  );
};

export default KhaltiTopupModal;
